import React, { ReactNode } from 'react';
import Spinner from '@indeed/frontend-components-react/components/Spinner';
import {
    Box,
    Button,
    InputFormField,
    InputFormFieldProps,
    TextAreaFormField,
    TextAreaFormFieldProps,
} from '@indeed/ifl-components';
import { useField } from 'formik';

interface SubmitButtonProps {
    isSubmitting: boolean;
    isLoading?: boolean;
    text?: string;
}

export const SubmitButton = ({ isSubmitting, isLoading, text = 'Submit' }: SubmitButtonProps): JSX.Element => {
    return (
        <Button full disabled={isSubmitting || isLoading} type="submit">
            {isSubmitting ? (
                <>
                    <Spinner color="dark" label="Loading" size="sm" /> Submiting ...
                </>
            ) : (
                text
            )}
        </Button>
    );
};

export type FormikInputFieldProps = Omit<InputFormFieldProps, 'ref'> & {
    label: ReactNode;
    fieldName: string;
    inputType?: 'text' | 'number' | 'time' | 'date';
};

export const FormikInputField = ({
    fieldName,
    label,
    inputType = 'text',
    ...otherProps
}: FormikInputFieldProps): JSX.Element => {
    const [field, meta, helpers] = useField(fieldName);

    return (
        <Box sx={{ paddingBlockEnd: 25 }}>
            <InputFormField
                label={label}
                inputProps={{
                    type: inputType,
                    value: field.value || '',
                    onChange: (e) => helpers.setValue(e.target.value),
                }}
                invalid={!!meta.error && meta.touched}
                errorText={meta.error}
                {...otherProps}
            />
        </Box>
    );
};

export type FormikInputDateFieldProps = Omit<InputFormFieldProps, 'ref'> & {
    label: ReactNode;
    fieldName: string;
    min: string;
};

export const FormikInputDateField = ({
    fieldName,
    label,
    min,
    ...otherProps
}: FormikInputDateFieldProps): JSX.Element => {
    const [field, meta, helpers] = useField(fieldName);

    return (
        <Box sx={{ paddingBlockEnd: 25 }}>
            <InputFormField
                label={label}
                inputProps={{
                    type: 'date',
                    value: field.value || '',
                    min,
                    onChange: (e) => helpers.setValue(e.target.value),
                }}
                invalid={!!meta.error && meta.touched}
                errorText={meta.error}
                {...otherProps}
            />
        </Box>
    );
};

export type FormikTextAreaProps = TextAreaFormFieldProps & {
    label: ReactNode;
    fieldName: string;
    helperText: string;
};

export const FormikTextAreaField = ({
    fieldName,
    label,
    helperText,
    ...otherProps
}: FormikTextAreaProps): JSX.Element => {
    const [field, ,] = useField(fieldName);

    return (
        <TextAreaFormField
            {...field}
            name={fieldName}
            label={label}
            helperText={helperText}
            size="md"
            style={{ marginBottom: '25px' }}
            {...otherProps}
        />
    );
};
